import { useCallback, useEffect, useState } from 'react'
import Select, {
  ClassNamesConfig,
  components,
  MultiValue,
  MultiValueRemoveProps,
  OptionProps,
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import makeAnimated from 'react-select/animated'
import Tippy from '@tippyjs/react'

import { Option } from '../../../types/option'

import Icon from '../../ui/Icon'

interface MultiSelectInputProps {
  id?: string
  name: string
  value: Option[]
  options: Option[]
  required?: boolean
  label?: string
  placeHolder?: string
  disabled?: boolean
  loading?: boolean
  infoMessage?: string
  errorMessage?: string
  className?: string
  fitContent?: boolean
  creatable?: boolean
  lockedValues?: string[]
  onChange: (name: string, values: string[], isValid: boolean) => void
  onValidate?: (values: string[]) => boolean
  onCreate?: (inputValue: string) => Promise<Option>
  onDelete?: (option: Option) => Promise<void>
}

const animatedComponents = makeAnimated()

const multiSelectClassNames: ClassNamesConfig<Option, true> = {
  control: state =>
    [
      'multi-select-control',
      state.isFocused && 'is-focused',
      state.isDisabled && 'is-disabled',
    ]
      .filter(Boolean)
      .join(' '),

  valueContainer: () => 'multi-select-value-container',
  input: () => 'multi-select-text-input',
  placeholder: () => 'multi-select-placeholder',

  multiValue: () => 'multi-select-value',
  multiValueLabel: () => 'multi-select-value-label',
  multiValueRemove: () => 'multi-select-value-remove',

  indicatorsContainer: () => 'multi-select-indicators',
  indicatorSeparator: () => 'multi-select-indicator-separator',
  dropdownIndicator: () => 'multi-select-indicator',
  clearIndicator: () => 'multi-select-indicator',
  loadingIndicator: () => 'multi-select-loading-indicator',

  menuPortal: () => 'multi-select-menu-portal',
  menu: () => 'multi-select-menu',
  menuList: () => 'multi-select-menu-list',

  option: state =>
    [
      'multi-select-option',
      state.isFocused && 'is-focused',
      state.isSelected && 'is-selected',
    ]
      .filter(Boolean)
      .join(' '),

  noOptionsMessage: () => 'multi-select-message',
  loadingMessage: () => 'multi-select-message',
}

export const MultiSelectInput = ({
  id,
  name,
  label,
  value,
  options,
  required = false,
  placeHolder = '',
  disabled = false,
  loading = false,
  infoMessage,
  errorMessage,
  className,
  fitContent = false,
  creatable = false,
  lockedValues = [],
  onChange,
  onValidate,
  onCreate,
  onDelete,
}: MultiSelectInputProps) => {
  const [selected, setSelected] = useState<Option[]>(value)
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)

  useEffect(() => {
    setSelected(value)
  }, [value])

  const isLocked = useCallback(
    (option: Option) => lockedValues.includes(option.value),
    [lockedValues]
  )

  const updateSelected = useCallback(
    (updated: Option[]) => {
      const lockedSelected = selected.filter(isLocked)

      const removableSelected = updated.filter(option => !isLocked(option))

      const nextSelected = [...lockedSelected, ...removableSelected]

      const values = nextSelected.map(option => option.value)
      const hasValue = values.length > 0
      const customValid = onValidate ? onValidate(values) : true
      const valid = (!required || hasValue) && customValid

      setSelected(nextSelected)
      setIsError(hasValue && !customValid)

      onChange(name, values, valid)
    },
    [selected, isLocked, name, required, onChange, onValidate]
  )

  const handleChange = (updated: MultiValue<Option>) => {
    setIsTouch(true)
    updateSelected([...updated])
  }

  const handleCreate = async (inputValue: string) => {
    if (!onCreate) {
      return
    }

    setIsTouch(true)

    const newOption = await onCreate(inputValue)

    updateSelected([...selected, newOption])
  }

  const handleDelete = async (option: Option) => {
    if (isLocked(option)) {
      return
    }

    if (onDelete) {
      await onDelete(option)
    }

    setIsTouch(true)

    updateSelected(
      selected.filter(selectedOption => selectedOption.value !== option.value)
    )
  }

  const CustomOption = (props: OptionProps<Option, true>) => {
    const { data } = props

    return (
      <components.Option {...props}>
        <div className="d-flex justify-content-between align-items-center w-100">
          <span>{data.label}</span>

          {onDelete && !isLocked(data) && (
            <button
              type="button"
              className="multi-select-delete"
              onClick={event => {
                event.stopPropagation()
                void handleDelete(data)
              }}
            >
              ×
            </button>
          )}
        </div>
      </components.Option>
    )
  }

  const CustomMultiValueRemove = (
    props: MultiValueRemoveProps<Option, true>
  ) => {
    if (isLocked(props.data)) {
      return null
    }

    return <components.MultiValueRemove {...props} />
  }

  const isRequired = isTouch && required && selected.length === 0

  const hasRemovableValues = selected.some(option => !isLocked(option))

  const fieldClass = [
    'field',
    'multi-select-input',
    fitContent && 'multi-select-fit-content',
    className,
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  const inputId = id ?? name
  const isCreatable = creatable && Boolean(onCreate)

  const selectProps = {
    inputId,
    name,
    isMulti: true as const,
    unstyled: true,
    required,
    isDisabled: disabled,
    isLoading: loading,
    options,
    value: selected,
    placeholder: !disabled ? placeHolder : '',
    closeMenuOnSelect: false,
    hideSelectedOptions: true,
    isClearable: !required && hasRemovableValues,
    menuPortalTarget: document.body,
    classNames: multiSelectClassNames,
    components: {
      ...animatedComponents,
      Option: CustomOption,
      MultiValueRemove: CustomMultiValueRemove,
    },
    onChange: handleChange,
    onFocus: () => setIsFocus(true),
    onBlur: () => {
      setIsFocus(false)
      setIsTouch(true)
    },
  }

  return (
    <div className={fieldClass}>
      {label && <label htmlFor={inputId}>{label}</label>}
      <div className="input-icon-wrap">
        {isCreatable ? (
          <CreatableSelect<Option, true>
            {...selectProps}
            onCreateOption={handleCreate}
          />
        ) : (
          <Select<Option, true> {...selectProps} />
        )}

        {infoMessage && (
          <Tippy content={infoMessage} placement="top" className="ph-tooltip">
            <div className="input-icon-toggle">
              <Icon name="info" size={16} />
            </div>
          </Tippy>
        )}
      </div>

      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>

          {errorMessage}
        </div>
      )}
    </div>
  )
}
